import zabytek1Img from "../../assets/placeholders/Zabytek1.png";
import SubkowyMainPageWhereImg from "../../assets/placeholders/SubkowyMainPageWhere.png";
import SubkowyMainPageNextImg from "../../assets/placeholders/SubkowyMainPageNext.png";
import LogoTypeAddedImg from "../../assets/placeholders/LogoTypeAdded.png";
import HomeHeroImg from "../../assets/placeholders/MainHeroImg.png";
import zabytek2Img from "../../assets/placeholders/Zabytek2.png";
import zabytek3Img from "../../assets/placeholders/Zabytek3.png";
import zabytek4Img from "../../assets/placeholders/Zabytek4.png";
import videoCoverImg from "../../assets/placeholders/CoverVideo.png";
import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import BusinessCardModal from "../guide/BusinessCardModal";
import CardPillLabel from "../../components/ui/CardPillLabel";
import HeroArticle from "../../components/common/HeroArticle";
import RouteMap from "../map/components/RouteMap";
import ImageModal from "../media/components/ImageModal";
import VideoModal from "../media/components/VideoModal";
import { timelineItems } from "../timeline/data/timelineItems";
import { IconOpenExternal, IconMapPin, IconArrowCircleRight, IconWindRose } from "../../components/ui/Icons";
import {
  SLUBKI,
  FIXED_ROUTE_POINTS,
  distanceMeters,
  formatDistance,
} from "../map/utils/mapHelpers";
import { IconFullscreen } from "../../components/ui/Icons";
import { ROUTE_START } from "../map/data/routePoints";
import AnimatedUnderline from "../../components/ui/AnimatedUnderline";

const MotionLink = motion(Link);

const routeImages = [
  { src: SubkowyMainPageWhereImg, alt: "Subkowy Początek" },
  { src: SubkowyMainPageNextImg, alt: "Dęby" },
  { src: zabytek2Img, alt: "Zabytek 2" },
  { src: zabytek3Img, alt: "Zabytek 3" },
  { src: zabytek4Img, alt: "Zabytek 4" },
];
const timelineImageBySlug = Object.fromEntries(
  timelineItems.map((item) => [item.slug, { src: item.image, alt: item.title }])
);

const videoPlaceholderSrc = videoCoverImg;
const demoVideoSrc =
  "https://pub-d1390f28d2f045c8b066c892298b592e.r2.dev/KerinPresentation.mp4";

export default function HomePage({ initialSection }) {
  const [loc] = useState(SLUBKI);
  const points = FIXED_ROUTE_POINTS;
  const [openImg, setOpenImg] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const videoWrapRef = useRef(null);
  const [loadVideo, setLoadVideo] = useState(false);
  const location = useLocation();

  const opisBody =
    "Znajdujesz się w Subkowach, kociewskiej miejscowości położonej na Pomorzu, w powiecie tczewskim. To właśnie tutaj rozpoczyna się szlak „Dzieje według Kerin”, który łączy miejsca, obrazy i opowieści w jedną historyczną trasę. Strona, którą widzisz, pomaga poruszać się po kolejnych punktach na miejscu, a jednocześnie pozwala odkrywać je także zdalnie. Możesz potraktować ją jako przewodnik w czasie spaceru albo jako zaproszenie do odwiedzenia Subków i przeżycia tej podróży osobiście.";

  const center = useMemo(() => [loc.lat, loc.lng], [loc.lat, loc.lng]);

  const routePointItems = useMemo(() => {
    return [ROUTE_START, ...points.filter((point) => point.id !== ROUTE_START.id)];
  }, [points]);

  const routeSegments = useMemo(() => {
    const nodes = routePointItems;
    const segments = [];

    for (let index = 0; index < nodes.length - 1; index += 1) {
      const from = nodes[index];
      const to = nodes[index + 1];
      const meters = distanceMeters(from, to);

      segments.push({
        id: `${from.id}-${to.id}`,
        from: from.title,
        to: to.title,
        meters,
        text: formatDistance(meters),
      });
    }

    const total = segments.reduce((sum, item) => sum + item.meters, 0);

    return {
      segs: segments,
      totalText: formatDistance(total),
    };
  }, [routePointItems]);

  useEffect(() => {
    const requested =
      location.state?.scrollTo ||
      (initialSection === "epoki"
        ? "section-epoki"
        : initialSection === "miejsca"
        ? "section-miejsca"
        : null);

    if (!requested) return;

    const timer = window.setTimeout(() => {
      const element = document.getElementById(requested);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 120);

    return () => window.clearTimeout(timer);
  }, [initialSection, location.state]);

  useEffect(() => {
    const element = videoWrapRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting) {
          setLoadVideo(true);
          observer.disconnect();
        }
      },
      { root: null, threshold: 0.15 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const openPointImage = (point) => {
  const mappedImage = timelineImageBySlug[point.slug];

  if (!mappedImage) return;

  setOpenImg({
    src: mappedImage.src,
    alt: mappedImage.alt,
  });
  };

  return (
    <section className="pageSection homeSection">
    <div className="container">
      <div className="homeHeroIntro">
        <div className="homeHeroIntroInner">
          <div className="homeHeroKicker">Projekt historyczny</div>

          <h1 className="homeHeroTitle">
            <span className="homeHeroTitleBlue">Subkowy</span>{" "}
            <span className="homeHeroTitleDark">Projekt</span>
          </h1>

          <div className="homeHeroBadge">
            Projekt historyczny, 
            który pozwala odkrywać miejsca z zupełnie nowej perspektywy.
            To zaproszenie do poznania historii zapisanej w przestrzeni, 
            ludziach i wydarzeniach, które wciąż pozostawiają swój ślad.
          </div>
        </div>
      </div>

      <div className="grid2">
          <div className="leftCol">
            <div className="card cardLabeled" id="section-miejsca">
              <CardPillLabel text="Szlak historyczny" variant="miejsca" />
              <h1 className="h1">
                Witamy na historycznej{' '}
                <span className="noBreak">trasie{' '}
                  <button
                    type="button"
                    className="guideInlineLink"
                    onClick={() => setGuideOpen(true)}
                  >
                  „Dzieje według Kerin”
                  </button>
                </span>
              </h1>

              <p className="mutedSmall" style={{ marginTop: 6 }}>
                Ta strona została przygotowana jako mapa i przewodnik po szlaku w Subkowach. 
                Jeśli jesteś już na miejscu, przeprowadzi Cię przez kolejne stacje i pomoże odkrywać ich znaczenie, krok po kroku.
                Jeśli trafiłeś tutaj z internetu, możesz poznać trasę wirtualnie, a potem odwiedzić Subkowy i przejść ją osobiście.
              </p>

              <div className="statusRow">
                <AnimatedUnderline as="span" className="pill ok" delay={0.1}>Początek trasy: Subkowy parking</AnimatedUnderline>
                <AnimatedUnderline as="span" className="pill" delay={0.2}>
                  Punkty na mapie: <strong>{points.length}</strong>
                </AnimatedUnderline>
              </div>
            </div>

            <HeroArticle
              imgSrc={HomeHeroImg}
              tagText="PUNKT STARTOWY TRASY"
              title="Subkowy, początek magicznej podróży"
              titleIcon={<IconWindRose />}
              subtitle="Mapa szlaku i wirtualny przewodnik po „Dziejach według Kerin”"
              body={opisBody}
              meta="Subkowy, trasa historyczna"
              childrenBelow={
                <>
                  <div className="gallery">
                    {routeImages.slice(0, 2).map((image) => (
                      <button
                        key={image.alt}
                        className="imgBtn"
                        onClick={() =>
                          setOpenImg({ src: image.src, alt: image.alt })
                        }
                        aria-label={`Otwórz zdjęcie: ${image.alt}`}
                      >
                        <img className="img" src={image.src} alt={image.alt} />
                      </button>
                    ))}
                  </div>

                  <div className="infoGrid">
                    <motion.div
                      className="infoBox"
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.25 }}
                      transition={{ duration: 1.6, ease: "easeOut" }}
                    >
                      <span className="infoBoxIcon" style={{ color: "#2563eb" }}><IconMapPin /></span>
                      <CardPillLabel text="Gdzie się znajdujemy" variant="mapa" />
                      <div className="infoValue">
                        Jesteśmy w Subkowach, w miejscu, z którego rozpoczyna się historyczny szlak „Dzieje według Kerin”. To tutaj zaczyna się trasa prowadząca przez kolejne stacje, symbole i opowieści związane z historią tego miejsca.
                      </div>
                    </motion.div>

                    <motion.div
                      className="infoBox"
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.25 }}
                      transition={{ duration: 1.6, ease: "easeOut", delay: 0.15 }}
                    >
                      <span className="infoBoxIcon" style={{ color: "#d97706" }}><IconArrowCircleRight /></span>
                      <CardPillLabel text="Co dalej" variant="lista" />
                      <div className="infoValue">
                        Jeśli jesteś na miejscu, skorzystaj z mapy i przejdź do kolejnych punktów szlaku. Jeśli odwiedzasz stronę zdalnie, możesz rozpocząć wirtualną podróż, poznać stacje trasy i zaplanować własną wizytę w Subkowach.
                      </div>
                    </motion.div>
                  </div>
                </>
              }
            />

            <div className="card cardLabeled" id="section-epoki">
              <CardPillLabel text="Na początek wędrówki" variant="lista" />

              <div className="routeMeta" style={{ marginTop: 2 }}>
                <span className="pill">
                  Łącznie: <strong>{routeSegments.totalText}</strong>
                </span>
                <span className="pill">
                  Odcinki: <strong>{routeSegments.segs.length}</strong>
                </span>
              </div>

              <div style={{ marginTop: 12 }}>
                <p className="mutedSmall" style={{ marginBottom: 10 }}>
                  Ta strona jest zaproszeniem do przejścia całego szlaku, zarówno na miejscu, jak i w formie wirtualnej. Znajdziesz tutaj mapę, zdjęcia, film oraz kolejne punkty, które prowadzą przez historię Subków i pomagają odkrywać sens każdej stacji.
                </p>

                <p className="mutedSmall" style={{ marginBottom: 0 }}>
                  Jeśli jesteś na trasie, ruszaj do następnych miejsc i korzystaj z tej strony jak z przewodnika. Jeśli dopiero poznajesz projekt, przejdź szlak online i zobacz, jaką podróż możesz odbyć podczas wizyty w Subkowach.
                </p>
              </div>

              <div className="routeList" role="list" style={{ marginTop: 16 }}>
                {routePointItems.map((point, index) => (
                  <MotionLink
                    to={`/sciezka-czasu/${point.slug}`}
                    className="routeRow routeRowLink"
                    role="listitem"
                    key={point.id}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.8, ease: "easeOut", delay: index * 0.12 }}
                  >
                    <div className="routeIdx">{point.order}</div>

                    <div className="routeMain">
                      <div className="routeTitle">{point.title}</div>

                      <div className="routeSub mutedSmall">
                        {point.shortDescription}
                      </div>
                    </div>

                    <div className="routeKm">Przejdź</div>
                  </MotionLink>
                ))}
              </div>
            </div>
          </div>

          <div className="rightSticky">
            <div className="card cardLabeled">
              <CardPillLabel text="Mapa trasy" variant="mapa" />
              <p className="mutedSmall" style={{ marginBottom: 10 }}>
                Mapa pokazuje kolejne punkty szlaku „Dzieje według Kerin”. Możesz potraktować ją jako pomoc podczas spaceru po Subkowach albo jako wprowadzenie do wirtualnego zwiedzania.
              </p>

              <RouteMap
                  center={center}
                  startPoint={ROUTE_START}
                  points={points}
                  onShowStartImage={() => {
                    const mappedImage = timelineImageBySlug[ROUTE_START.slug];

                    if (!mappedImage) return;

                    setOpenImg({
                      src: mappedImage.src,
                      alt: mappedImage.alt,
                    });
                  }}
                  onShowPointImage={openPointImage}
              />

              <div className="row gap wrap" style={{ marginTop: 10 }}>
                <a
                  className="btn"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    `${loc.lat},${loc.lng}`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Otwórz Subkowy w Mapach
                </a>
              </div>
            </div>

            <div className="card cardLabeled">
              <CardPillLabel text="Film" variant="film" />

              <div className="videoWrap" ref={videoWrapRef} style={{ marginTop: 6 }}>
                {!loadVideo ? (
                  <div
                    className="videoPlaceholder"
                    role="status"
                    aria-label="Ładowanie filmu"
                  >
                    Film ładuje się, przewiń kawałek niżej lub chwilę poczekaj
                  </div>
                ) : (
                  <video
                    className="videoPlayer"
                    src={demoVideoSrc}
                    controls
                    playsInline
                    preload="metadata"
                    poster={videoPlaceholderSrc}
                  >
                    Twoja przeglądarka nie wspiera odtwarzania wideo.
                  </video>
                )}
              </div>

              <div className="row gap wrap" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn btnIcon"
                  onClick={() => setVideoOpen(true)}
                >
                  <IconFullscreen />
                  Otwórz film na pełnym ekranie
                </button>

            {/* <a
            className="btn btnIcon"
            href={demoVideoSrc}
            target="_blank"
            rel="noreferrer"
            >
            <IconOpenExternal />
            Otwórz film w nowej karcie
                </a> */}
              </div>
            </div>
          </div>
        </div>
         <div className="card">
        <h2 className="h2" style={{ marginBottom: 10 }}>
          Dofinansowanie projektu
        </h2>

        <p
          className="pageLead"
          style={{
            marginBottom: 16,
            textAlign: "center",
            maxWidth: 780,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          „Dzieje Europy w Grodzie Kerin” , organizacja targów rzemiosła z
          wykorzystaniem interaktywnej trasy edukacyjno turystycznej jako klucz
          do dywersyfikacji działalności firmy Nowakowski Robert Agencja
          Artystyczna „KERIN” (Region 1 , pomorskie) w okresie od 01.01.2026 r.
          do 31.03.2026 r.
        </p>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <img
            src={LogoTypeAddedImg}
            alt="Logotypy programu dofinansowania"
            style={{
              width: "100%",
              maxWidth: 760,
              height: "auto",
              display: "block",
              borderRadius: 4,
            }}
          />
        </div>
      </div>
      </div>

      {openImg && (
        <ImageModal
          src={openImg.src}
          alt={openImg.alt}
          onClose={() => setOpenImg(null)}
        />
      )}

      {videoOpen && (
        <VideoModal
          src={demoVideoSrc}
          onClose={() => setVideoOpen(false)}
        />
      )}

      <BusinessCardModal
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
      />
    </section>
  );
}